/**
 * UserInfoModal - A modal component that prompts the user to enter their email and full name.
 *
 * This component is used to collect user information and used as a wrapper for inner components.
 *
 * Created by Ron on 2024/08/22.
 * Copyright (c) 2024-present iWoo Ltd. All rights reserved.
 */
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Modal from "react-modal";

import { useSignUpState } from "../../state/SignUpState";
import { useAppState } from "../../state";

// Components
import SetEmail from "../SignUp/SetEmail/SetEmail";
import Registration from "../SignUp/Registration/Registration";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Icons
import CloseIcon from "../../icons/CloseIcon";

// Utils
import { clientWidth } from "../../utils";
import { modalStyles } from "../../constants";

const UserInfoModal = ({ children, isOpen, onSubmit, onClose }) => {
  const location = useLocation();
  const history = useHistory();
  const { theme } = useTheme();

  const {
    user,
    hasTriedToAutoLogin,
    isLoggedIn,
    questionnaire,
    getQuestionnaire,
    updateCustomerInfo,
    questionnaireState,
    setQuestionnaireState,
    userDoB,
    gender,
    setGender,
    userPreferredWorkouts,
    completeQuestionnaire,
  } = useAppState();

  const [_isOpen, setIsOpen] = useState(isOpen);

  const customStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? Object.assign(
          {},
          { ...modalStyles.mobileDefault },
          {
            overlay: {
              ...modalStyles.mobileDefault.overlay,
              zIndex: 1060,
            },
            content: {
              ...modalStyles.mobileDefault.content,
              padding: "24px",
            },
          }
        )
      : Object.assign(
          {},
          { ...modalStyles.default },
          {
            overlay: { ...modalStyles.default.overlay, zIndex: 1060 },
            content: {
              ...modalStyles.default.content,
              width: "428px",
              zIndex: 1060,
            },
          }
        );

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);

    onClose && onClose();
  };

  useEffect(() => {
    // Went from Success or Cancel pages
    const purchaseComplete = sessionStorage.getItem("purchaseComplete");

    if (
      !isLoggedIn ||
      purchaseComplete !== null ||
      !user ||
      location.pathname === "/" ||
      location.pathname === "/checkout"
    ) {
      if (isLoggedIn) {
        sessionStorage.removeItem("purchaseComplete");
      }

      return;
    }

    if (!user.email || user.firstName.includes("user")) {
      handleOpenModal();
    }
  }, [isLoggedIn]);

  const handleSubmit = (val) => {
    if (typeof val !== "object") {
      return;
    }

    if (user && val.email) {
      updateCustomerInfo(val, user.id).then(() => handleCloseModal());
    } else if (user) {
      updateCustomerInfo(val, user.id).then(
        () => user.email && handleCloseModal()
      );
    }
    // onSubmit(); // Call the onSubmit function passed as a prop
  };

  return (
    <div>
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={_isOpen}
        // onAfterOpen={this.afterOpenModal}
        onRequestClose={() => handleCloseModal()}
        style={customStyle}
        contentLabel="User Info Modal"
      >
        <button
          onClick={() => handleCloseModal()}
          className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-text-coral"
        >
          <CloseIcon />
        </button>
        {children ? (
          children
        ) : user && user.firstName.includes("user") ? (
          <Registration ctaText="Proceed" onNext={handleSubmit} />
        ) : (
          user &&
          !user.email && <SetEmail ctaText="Proceed" onNext={handleSubmit} />
        )}
      </Modal>
    </div>
  );
};

UserInfoModal.displayName = "UserInfoModal";

export default UserInfoModal;
